import * as React from "react";
import { planningRecordRepository } from "@/data/repositories";
import type { PlanningScope } from "@/data/repositories/planningRecordRepository";

export function usePlanningCollection<T extends { id: string }, TInput extends object>(
  scope: PlanningScope | null,
  seed: T[],
  build: (input: TInput, id: string) => T
) {
  const [items, setItems] = React.useState<T[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const scopeKey = scope ? JSON.stringify([scope.organizationId, scope.eventId, scope.module]) : null;
  const [loadedScopeKey, setLoadedScopeKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    let current = true;
    if (!scope) {
      setItems([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setItems([]);
    planningRecordRepository.list<T>(scope, seed).then((values) => {
      if (current) setItems(values);
    }).catch((error) => console.error("Unable to load planning records", error)).finally(() => {
      if (current) { setLoadedScopeKey(scopeKey); setIsLoading(false); }
    });
    return () => { current = false; };
  }, [scope, seed, scopeKey]);

  const create = React.useCallback((input: TInput) => {
    if (!scope) throw new Error("A planning scope is required");
    const value = build(input, crypto.randomUUID());
    setItems((previous) => [...previous, value]);
    void planningRecordRepository.save(scope, value).catch((error) => {
      setItems((previous) => previous.filter((item) => item.id !== value.id));
      console.error("Unable to save planning record", error);
    });
    return value;
  }, [scope, build]);

  const update = React.useCallback((id: string, patch: Partial<TInput> | ((value: T) => Partial<TInput>)) => {
    if (!scope) return;
    setItems((previous) => previous.map((item) => {
      if (item.id !== id) return item;
      const next = { ...item, ...(typeof patch === "function" ? patch(item) : patch) };
      void planningRecordRepository.save(scope, next).catch((error) => console.error("Unable to update planning record", error));
      return next;
    }));
  }, [scope]);

  const remove = React.useCallback((id: string) => {
    if (!scope) return;
    setItems((previous) => previous.filter((item) => item.id !== id));
    void planningRecordRepository.remove(scope, id).catch((error) => console.error("Unable to delete planning record", error));
  }, [scope]);

  return { items: scope && loadedScopeKey === scopeKey ? items : [], isLoading: isLoading || Boolean(scope && loadedScopeKey !== scopeKey), create, update, remove };
}
